import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import i18n from "../../i18n/i18n";
import { getApiErrorMessage } from "../../utils/apiErrorMessage";
const BASE_URL = process.env.REACT_APP_BASE_URL;

// all bonds
export const fetchBonds = createAsyncThunk(
  "bonds/fetchbonds",
  async ({ page = 1, filters = {} } = {}, { rejectWithValue }) => {
    try {
      let params = { page };

      if (filters.type) params.type = filters.type;
      if (filters.bond_number) params.bond_number = filters.bond_number;
      if (filters.beneficiary_name) params.beneficiary_name = filters.beneficiary_name;
      if (filters.user_type) params.user_type = filters.user_type;
      if (filters.user_id) params.user_id = filters.user_id;
      if (filters.payment_method) params.payment_method = filters.payment_method;
      if (filters.status_use) params.status_use = filters.status_use;
      if (filters.created_user_id) params.created_user_id = filters.created_user_id;

      
      if (
        Array.isArray(filters.amount_to_from) &&
        filters.amount_to_from[0] !== "" &&
        filters.amount_to_from[1] !== ""
      ) {
        params.amount_to_from = JSON.stringify([
          String(filters.amount_to_from[0]),
          String(filters.amount_to_from[1]),
        ]);
      }

      if (filters.send_to_customer !== "" && filters.send_to_customer != null) {
        params.send_to_customer = filters.send_to_customer;
      }

      if (filters.date_filter) {
        params.date_filter = filters.date_filter;

        if (filters.date_filter === "range") {
          if (filters.from_date) params.from_date = filters.from_date;
          if (filters.to_date) params.to_date = filters.to_date;
        }
      }

      const response = await axios.get(`${BASE_URL}/bonds`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
          Lang: i18n.language,
        },
        params,
      });

      return {
        bonds: response.data.data.data,
        pagination: response.data.data.pagination 
      };
    } catch (error) {
      const message = error.response?.data?.message || "Failed load data";
      return rejectWithValue(message);
    }
  }
);

export const fetchAllBonds = createAsyncThunk("bonds/fetchAllbonds", async () => {
  try {
    const response = await axios.get(`${BASE_URL}/bonds`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
        "Content-Type": "application/json",
        Lang: i18n.language,
      },
    });
    return response.data.data.data;
  } catch (error) {
    const message = error.response?.data?.message || "Failed load data";
    return message;
  }
});

// add bond
export const addBond = createAsyncThunk(
  "bonds/add",
  async (payload, { rejectWithValue }) => {
    try {
      const { attachments = [], ...fields } = payload;
      const formData = new FormData();

      Object.entries(fields).forEach(([key, value]) => {
        if (key === "auto_allocation") return;
        if (value === undefined || value === null || value === "") return;
        formData.append(key, value);
      });

      formData.append("auto_allocation", fields.auto_allocation ? "1" : "0");

      attachments.forEach((file, index) => {
        if (file instanceof File) {
          formData.append(`attachments[${index}]`, file);
        }
      });

      const response = await axios.post(`${BASE_URL}/bonds/store`, formData, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "multipart/form-data",
          Lang: i18n.language,
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        getApiErrorMessage(
          error.response?.data,
          error.message || "An unknown error occurred"
        )
      );
    }
  }
);
// get bond record
export const bondRecord = createAsyncThunk(
  "bonds/bondRecord",
  async (id, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${BASE_URL}/bonds/show/${id}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
          Lang: i18n.language,
        },
      });
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "An unknown error occurred";
      return rejectWithValue({ message });
    }
  }
);

const buildBondFormData = (fields, { attachments = [], bondId, includeBondId } = {}) => {
  const formData = new FormData();

  Object.entries(fields).forEach(([key, value]) => {
    if (key === "auto_allocation") return;
    if (value === undefined || value === null || value === "") return;
    formData.append(key, value);
  });

  formData.append("auto_allocation", fields.auto_allocation ? "1" : "0");

  if (includeBondId && bondId) {
    formData.append("bond_id", bondId);
  }

  attachments.forEach((file, index) => {
    if (file instanceof File) {
      formData.append(`attachments[${index}]`, file);
    }
  });

  return formData;
};

// update bond
export const updateBond = createAsyncThunk(
  "bonds/updateBond",
  async (payload, { rejectWithValue }) => {
    try {
      const {
        bond_id,
        includeBondId = true,
        attachments = [],
        ...fields
      } = payload;

      const formData = buildBondFormData(fields, {
        attachments,
        bondId: bond_id,
        includeBondId,
      });

      const response = await axios.post(`${BASE_URL}/bonds/update`, formData, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "multipart/form-data",
          Lang: i18n.language,
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        getApiErrorMessage(
          error.response?.data,
          error.message || "An unknown error occurred"
        )
      );
    }
  }
);

// delete bond (only when status is not_used)
export const deleteBond = createAsyncThunk(
  "bonds/deleteBond",
  async (bondId, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${BASE_URL}/bonds/delete`, null, {
        params: { bond_id: bondId },
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          Lang: i18n.language,
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        getApiErrorMessage(
          error.response?.data,
          error.message || "Failed to delete bond"
        )
      );
    }
  }
);

// delete bond file attachment
export const deleteBondAttachment = createAsyncThunk(
  "bonds/deleteBondAttachment",
  async (attachmentId, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append("attachment_id", attachmentId);

      const response = await axios.post(
        `${BASE_URL}/bonds/delete-attachment`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "multipart/form-data",
            Lang: i18n.language,
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        getApiErrorMessage(
          error.response?.data,
          error.message || "Failed to delete attachment"
        )
      );
    }
  }
);

/** Unlink bond from purchase invoice — POST /bonds/remove-purchase-allocation */
export const removeBondPurchaseAllocation = createAsyncThunk(
  "bonds/removePurchaseAllocation",
  async ({ bond_id, purchase_invoice_id }, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append("bond_id", String(bond_id));
      formData.append("purchase_invoice_id", String(purchase_invoice_id));

      const response = await axios.post(
        `${BASE_URL}/bonds/remove-purchase-allocation`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "multipart/form-data",
            Lang: i18n.language,
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        getApiErrorMessage(
          error.response?.data,
          error.message || "Failed to remove purchase allocation"
        )
      );
    }
  }
);

/** Unlink bond from sales invoice — POST /bonds/remove-sales-allocation */
export const removeBondSalesAllocation = createAsyncThunk(
  "bonds/removeSalesAllocation",
  async ({ bond_id, sales_invoice_id }, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append("bond_id", String(bond_id));
      formData.append("sales_invoice_id", String(sales_invoice_id));

      const response = await axios.post(
        `${BASE_URL}/bonds/remove-sales-allocation`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "multipart/form-data",
            Lang: i18n.language,
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        getApiErrorMessage(
          error.response?.data,
          error.message || "Failed to remove sales allocation"
        )
      );
    }
  }
);

const BondsSlice = createSlice({
  name: "bonds",
  initialState: {
    bonds: [],
    all_bonds: [],
    record: null,
    isLoading: false,
    failed: null,
    addedSuccess: null,
    failedUpdate: null,
    updatedSuccess: null,
    isDeleting: false,
    removeAllocationLoading: false,
  },
  reducers: {
    clearState: (state) => {
      state.error = null;
      state.success = null;
      state.failedUpdate = null;
      state.updatedSuccess = null;
      state.failed = null;
      state.addedSuccess = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchBonds.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchBonds.fulfilled, (state, action) => {
        state.isLoading = false;
        state.bonds = action.payload.bonds;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchBonds.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error?.message || "Failed to load data";
      })
      .addCase(fetchAllBonds.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAllBonds.fulfilled, (state, action) => {
        state.isLoading = false;
        state.all_bonds = action.payload;
      })
      .addCase(fetchAllBonds.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error?.message || "Failed to load data";
      })
      .addCase(addBond.pending, (state) => {
        state.isLoading = true;
        state.failed = null;
      })
      .addCase(addBond.fulfilled, (state, action) => {
        state.isLoading = false;
        state.addedSuccess = action.payload.message;
      })
      .addCase(addBond.rejected, (state, action) => {
        state.isLoading = false;
        state.failed = action.payload || "Failed to add data";
      })
      .addCase(bondRecord.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(bondRecord.fulfilled, (state, action) => {
        state.isLoading = false;
        state.record = action.payload.data;
      })
      .addCase(bondRecord.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error?.message || "Failed to add data";
      })
      .addCase(updateBond.pending, (state) => {
        state.isLoading = true;
        state.failedUpdate = null;
      })
      .addCase(updateBond.fulfilled, (state, action) => {
        state.isLoading = false;
        state.updatedSuccess = action.payload.message;
      })
      .addCase(updateBond.rejected, (state, action) => {
        state.isLoading = false;
        state.failedUpdate = action.payload || "Failed to update record";
      })
      .addCase(deleteBond.pending, (state) => {
        state.isDeleting = true;
      })
      .addCase(deleteBond.fulfilled, (state) => {
        state.isDeleting = false;
      })
      .addCase(deleteBond.rejected, (state) => {
        state.isDeleting = false;
      })
      .addCase(removeBondPurchaseAllocation.pending, (state) => {
        state.removeAllocationLoading = true;
      })
      .addCase(removeBondPurchaseAllocation.fulfilled, (state) => {
        state.removeAllocationLoading = false;
      })
      .addCase(removeBondPurchaseAllocation.rejected, (state) => {
        state.removeAllocationLoading = false;
      })
      .addCase(removeBondSalesAllocation.pending, (state) => {
        state.removeAllocationLoading = true;
      })
      .addCase(removeBondSalesAllocation.fulfilled, (state) => {
        state.removeAllocationLoading = false;
      })
      .addCase(removeBondSalesAllocation.rejected, (state) => {
        state.removeAllocationLoading = false;
      });
  },
});

export const { clearState } = BondsSlice.actions;
export default BondsSlice.reducer;
